import type { Item, List } from "./api.js";

/**
 * The categories a Category field suggests (g79): every one an item holds on a
 * Shopping list, completed items included, once each ignoring case, in the
 * person's locale's order. Nothing is kept beyond the items themselves, so a
 * value goes once no item holds it.
 *
 * Of two spellings that differ only in case, the one more items hold is kept,
 * and a tie goes to the one `localeCompare` puts first, so every device shows
 * the same spelling without a record of which was typed last.
 */
export function categorySuggestions(lists: readonly List[], items: readonly Item[]): string[] {
  const shopping = new Set(lists.filter((list) => list.kind === "shopping").map((list) => list.id));
  // Grouped by `toLowerCase`, which takes no locale, so the groups are the same on every device.
  const groups = new Map<string, Map<string, number>>();
  for (const item of items) {
    if (!item.category || !shopping.has(item.listId)) continue;
    const key = item.category.toLowerCase();
    const spellings = groups.get(key) ?? new Map<string, number>();
    spellings.set(item.category, (spellings.get(item.category) ?? 0) + 1);
    groups.set(key, spellings);
  }
  return [...groups.values()]
    .map((spellings) => [...spellings].sort(([a, many], [b, more]) => more - many || a.localeCompare(b))[0]![0])
    .sort((a, b) => a.localeCompare(b));
}
